import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BadgeCheck,
  CheckCircle2,
  Clock,
  FileCheck2,
  HardHat,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  RefreshCw,
  ShieldAlert,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { PageHead } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Loading,
  Modal,
  Progress,
  ScoreRing,
  Segmented,
  Stat,
  Stars,
  Textarea,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { dateLabel, money, num, phoneDial, relative, titleCase } from '../lib/format.js';

export default function Onboarding() {
  const navigate = useNavigate();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('applied,screening');
  const { data, loading, refresh } = useApi('/lumpers', { params: { status: statusFilter, sort: 'applied', dir: 'desc', limit: 100 } });
  const [selectedId, setSelectedId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const applicants = data || [];
  const selected = applicants.find((a) => a.id === selectedId) || applicants[0] || null;
  const { data: detail, refresh: refreshDetail } = useApi(`/lumpers/${selected?.id}/detail`, { skip: !selected });

  const act = async (status, note) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/lumpers/${selected.id}/status`, { status, note });
      toast.ok(`${selected.firstName} ${selected.lastName} → ${status}`);
      setRejectOpen(false);
      refresh();
      refreshDetail();
    } catch (err) {
      toast.err('Could not update the applicant', err.message);
    } finally {
      setBusy(false);
    }
  };

  const setDocStatus = async (docId, status) => {
    try {
      await api.patch(`/lumpers/documents/${docId}`, { status });
      refreshDetail();
      toast.ok(`Document ${status}`);
    } catch (err) {
      toast.err('Could not update the document', err.message);
    }
  };

  const documents = detail?.documents || [];
  const approvedDocs = documents.filter((d) => d.status === 'approved').length;
  const requiredDocs = documents.filter((d) => d.status !== 'not_required');
  const readyToApprove = requiredDocs.length > 0 && requiredDocs.every((d) => d.status === 'approved');

  return (
    <>
      <PageHead
        title="New people"
        subtitle="Have a look at who applied, chase their paperwork, and put them to work."
        icon={UserPlus}
        actions={
          <>
            <Segmented
              options={[
                { value: 'applied,screening', label: 'Waiting on us' },
                { value: 'applied', label: 'Applied' },
                { value: 'screening', label: 'Screening' },
                { value: 'rejected', label: 'Turned down' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
            />
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
          </>
        }
      />

      <div className="grid grid-3 mb-20">
        <Stat label="Waiting on us" icon={Clock} value={num(applicants.length)} tone="amber" foot="in this queue" />
        <Stat
          label="Paperwork all in"
          icon={FileCheck2}
          value={num(applicants.filter((a) => !a.missingDocs).length)}
          tone="green"
          foot="ready to start work"
        />
        <Stat
          label="Paperwork missing"
          icon={ShieldAlert}
          value={num(applicants.filter((a) => a.missingDocs > 0).length)}
          tone="red"
          foot="needs a follow-up"
        />
      </div>

      {!applicants.length && !loading ? (
        <Card>
          <EmptyState icon={CheckCircle2} title="Nothing in the onboarding queue">
            Every application has been worked. New lumpers show up here the moment they apply from the app or the website.
          </EmptyState>
        </Card>
      ) : (
        <div className="split-wide">
          <Card>
            <CardHead title="Who applied" subtitle={`${applicants.length} in this view`} icon={UserPlus} />
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Applicant</th>
                    <th className="narrow">Applied</th>
                    <th className="narrow">Coverage</th>
                    <th className="narrow">Profile</th>
                    <th className="narrow">Docs</th>
                    <th className="narrow">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {applicants.map((applicant) => (
                    <tr
                      key={applicant.id}
                      className={cx('clickable', selected?.id === applicant.id && 'selected')}
                      onClick={() => setSelectedId(applicant.id)}
                    >
                      <td>
                        <div className="row gap-10">
                          <Avatar name={`${applicant.firstName} ${applicant.lastName}`} tone={applicant.photoColor} />
                          <div className="col gap-2 truncate">
                            <span className="truncate" style={{ fontWeight: 600 }}>{applicant.firstName} {applicant.lastName}</span>
                            <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>
                              {applicant.kind === 'individual' ? 'Individual' : titleCase(applicant.kind)} · {applicant.referredBy || 'no referral'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="narrow dim">{relative(applicant.appliedAt)}</td>
                      <td className="narrow">
                        <span className="row gap-4 dim" style={{ fontSize: 'var(--fs-xs)' }}>
                          <MapPin size={11} /> {applicant.city || '—'}, {applicant.state || ''}
                        </span>
                      </td>
                      <td className="narrow">
                        <div className="row gap-8">
                          <Progress value={applicant.completeness} thin className="grow" />
                          <span className="num dim" style={{ fontSize: 'var(--fs-xs)' }}>{Math.round(applicant.completeness)}%</span>
                        </div>
                      </td>
                      <td className="narrow">
                        {applicant.missingDocs ? (
                          <Badge tone="red" size="sm">{applicant.missingDocs} missing</Badge>
                        ) : (
                          <Badge tone="green" size="sm" dot>complete</Badge>
                        )}
                      </td>
                      <td className="narrow"><Badge tone={applicant.status === 'screening' ? 'blue' : 'amber'} size="sm">{titleCase(applicant.status)}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {selected ? (
            <div className="col gap-20">
              <Card>
                <CardHead
                  title={`${selected.firstName} ${selected.lastName}`}
                  subtitle={`${selected.code} · applied ${dateLabel(selected.appliedAt)}`}
                  actions={<Link to={`/app/lumpers/${selected.id}`}><Button size="sm" variant="ghost">Full profile</Button></Link>}
                />
                <CardBody>
                  <div className="row between gap-16">
                    <div className="col gap-10 grow">
                      <span className="row gap-8 wrap">
                        <Badge tone={selected.status === 'screening' ? 'blue' : 'amber'}>{titleCase(selected.status)}</Badge>
                        {selected.experienceYears ? <Badge tone="muted">{selected.experienceYears} yrs experience</Badge> : null}
                        <Badge tone={selected.hasVehicle ? 'green' : 'muted'}>{selected.hasVehicle ? 'Has vehicle' : 'No vehicle'}</Badge>
                        <Badge tone={selected.hasPpe ? 'green' : 'muted'}>{selected.hasPpe ? 'Has PPE' : 'No PPE'}</Badge>
                      </span>
                      <div className="row gap-12 wrap">
                        <a href={`tel:${phoneDial(selected.phone)}`} className="row gap-6 dim" style={{ fontSize: 'var(--fs-sm)' }}>
                          <Phone size={13} /> {selected.phone || '—'}
                        </a>
                        <a href={`mailto:${selected.email}`} className="row gap-6 dim" style={{ fontSize: 'var(--fs-sm)' }}>
                          <Mail size={13} /> {selected.email}
                        </a>
                      </div>
                    </div>
                    <ScoreRing value={selected.completeness} tone={selected.completeness >= 90 ? 'green' : 'amber'} size={62} label={`${Math.round(selected.completeness)}%`} />
                  </div>

                  <div className="divider" />
                  <div className="label mb-8">Skills claimed</div>
                  <div className="wrap-gap">
                    {(selected.skills || []).map((skill) => <Badge key={skill} tone="amber" size="sm">{skill}</Badge>)}
                    {!selected.skills?.length ? <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Nothing claimed yet.</span> : null}
                  </div>
                  <div className="label mb-8 mt-16">Equipment</div>
                  <div className="wrap-gap">
                    {(selected.equipment || []).map((item) => <Badge key={item} tone="blue" size="sm">{item}</Badge>)}
                    {!selected.equipment?.length ? <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Nothing claimed yet.</span> : null}
                  </div>
                </CardBody>
                <div className="card-foot">
                  <div className="row gap-8 wrap">
                    <Button variant="primary" size="sm" icon={BadgeCheck} loading={busy} onClick={() => act('active', 'Approved during onboarding review')}>
                      Take them on
                    </Button>
                    <Button variant="outline" size="sm" icon={Clock} loading={busy} onClick={() => act('screening', 'Moved to screening — verifying references')}>
                      Start checking them
                    </Button>
                    <Button variant="danger" size="sm" icon={XCircle} onClick={() => setRejectOpen(true)}>Turn down</Button>
                  </div>
                </div>
              </Card>

              <Card>
                <CardHead
                  title="Paperwork checklist"
                  subtitle={`${approvedDocs} of ${requiredDocs.length} documents verified`}
                  icon={FileCheck2}
                />
                <CardBody className="col gap-10">
                  {readyToApprove ? (
                    <Callout tone="green" icon={CheckCircle2} title="Everything required is on file">
                      This applicant is ready to move onto the active roster.
                    </Callout>
                  ) : (
                    <Callout tone="amber" icon={ShieldAlert} title="Paperwork still outstanding">
                      Chase the items below before activating — active lumpers must have a W-9, ID and cleared background screen.
                    </Callout>
                  )}
                  {documents.map((doc) => (
                    <div key={doc.id} className="row between gap-10">
                      <span className="row gap-8">
                        <Badge
                          size="sm"
                          tone={doc.status === 'approved' ? 'green' : doc.status === 'pending' ? 'amber' : doc.status === 'not_required' ? 'muted' : 'red'}
                        >
                          {titleCase(doc.status)}
                        </Badge>
                        <span style={{ fontSize: 'var(--fs-sm)' }}>{doc.label}</span>
                      </span>
                      <span className="row gap-6">
                        <Button size="sm" variant="ghost" onClick={() => setDocStatus(doc.id, 'approved')} disabled={doc.status === 'approved'}>Looks right</Button>
                        <Button size="sm" variant="ghost" onClick={() => setDocStatus(doc.id, 'pending')} disabled={doc.status === 'pending'}>Not in yet</Button>
                      </span>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </div>
          ) : (
            <Card><Loading rows={6} /></Card>
          )}
        </div>
      )}

      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Turn this person down"
        subtitle={selected ? `${selected.firstName} ${selected.lastName}` : ''}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <RejectConfirm onConfirm={(reason) => act('rejected', reason)} loading={busy} />
          </>
        }
      >
        <RejectBody />
      </Modal>
    </>
  );
}

function RejectBody() {
  return (
    <Field label="Reason" hint="Kept on the application record for compliance.">
      <Textarea id="reject-reason" rows={3} placeholder="Failed to respond after three follow-ups…" />
    </Field>
  );
}

function RejectConfirm({ onConfirm, loading }) {
  return (
    <Button
      variant="danger"
      loading={loading}
      onClick={() => {
        const value = document.getElementById('reject-reason')?.value;
        onConfirm(value || 'Rejected during onboarding review');
      }}
    >
      Turn them down
    </Button>
  );
}
